import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import Input from '../components/Input';
import api, { getCountries, Country } from '../services/api';
import { t } from '../i18n';
import './ProfilePage.css';
import './AdminCreatorDetailPage.css';

const AdminCreatorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [creator, setCreator] = useState<any>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resending, setResending] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    phone: '',
    country: '',
    description: '',
    app: '',
    url_app: '',
    url_youtube: '',
    url_instagram: '',
    url_other: '',
    emailVerified: false,
    hide: 0,
    isTrusted: 0
  });

  useEffect(() => {
    const loadCreator = async () => {
      try {
        setLoading(true);
        const [{ data }, countriesRes] = await Promise.all([
          api.get(`/admin/creators/${id}`),
          getCountries()
        ]);
        setCountries(countriesRes.data);
        setCreator(data);
        setFormData({
          name: data.name || '',
          alias: data.alias || '',
          phone: data.phone || '',
          country: data.country || '',
          description: data.bio || '',
          app: data.app || '',
          url_app: data.url_app || '',
          url_youtube: data.url_youtube || '',
          url_instagram: data.url_instagram || '',
          url_other: data.url_other || '',
          emailVerified: !!data.emailVerified,
          hide: data.hide || 0,
          isTrusted: data.isTrusted || 0
        });
      } catch (err) {
        setError(t('adminCreatorDetail.error.load'));
      } finally {
        setLoading(false);
      }
    };
    loadCreator();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: name === 'hide' || name === 'isTrusted' ? (checked ? 1 : 0) : checked }));
  };

  const handleResendEmail = async () => {
    setResending(true);
    try {
      await api.post(`/admin/creators/${id}/resend-verification`);
      setSuccess(t('adminCreatorDetail.success.verificationSent'));
      setError('');
    } catch (err) {
      setError(t('adminCreatorDetail.error.verificationSend'));
    } finally {
      setResending(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put(`/admin/creators/${id}`, formData);
      setSuccess(t('adminCreatorDetail.success.updated'));
    } catch (err) {
      setError(t('adminCreatorDetail.error.update'));
    } finally {
      setSaving(false);
    }
  };

  const selectedCountry = formData.country
    ? countries.find(c => c.code === formData.country)
    : null;

  if (loading) {
    return (
      <div className="profile-page admin-creator-page">
        <Navbar />
        <div className="profile-container admin-creator-container">
          <div className="loading">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page admin-creator-page">
      <Navbar />
      <div className="profile-container admin-creator-container">
        <div className="profile-header admin-creator-header">
          <div>
            <h1 className="profile-title">{t('adminCreatorDetail.title')} {creator?.alias}</h1>
            <p className="profile-subtitle">{creator?.email}</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/admin/creators')}>
            {t('adminCreatorDetail.back')}
          </Button>
        </div>

        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">{success}</div>}

        <form onSubmit={handleSave} className="profile-form">
          <section className="form-section">
            <h2 className="section-title">{t('adminCreatorDetail.title')}</h2>

            <div className="admin-creator-grid">
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.id')}</label>
                <input value={creator.id} disabled className="form-input-disabled" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.email')}</label>
                <input value={creator.email} disabled className="form-input-disabled" />
              </div>
            </div>

            <div className="admin-creator-grid">
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.alias')}</label>
                <Input name="alias" value={formData.alias} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.fullName')}</label>
                <Input name="name" value={formData.name} onChange={handleChange} />
              </div>
            </div>

            <div className="admin-creator-grid">
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.phone')}</label>
                <Input name="phone" value={formData.phone} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.countryCode')}</label>
                <Select
                  options={countries.map(c => ({
                    value: c.code,
                    label: `${c.code} - ${c.name} (${c.full_name})`
                  }))}
                  value={selectedCountry ? {
                    value: selectedCountry.code,
                    label: `${selectedCountry.code} - ${selectedCountry.name} (${selectedCountry.full_name})`
                  } : null}
                  onChange={(option) => setFormData(prev => ({ ...prev, country: option?.value || '' }))}
                  isClearable
                  isSearchable
                  placeholder={t('adminCreatorDetail.countryPlaceholder')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-title">{t('adminCreatorDetail.flags')}</h2>

            <div className="admin-creator-flags">
              <label className="admin-creator-toggle">
                <input
                  type="checkbox"
                  id="emailVerified"
                  name="emailVerified"
                  checked={!!formData.emailVerified}
                  onChange={handleCheckboxChange}
                />
                <span>{t('adminCreatorDetail.emailVerified')}</span>
              </label>

              <Button type="button" variant="secondary" onClick={handleResendEmail} disabled={resending}>
                {resending ? t('adminCreatorDetail.sending') : t('adminCreatorDetail.resendEmail')}
              </Button>

              <label className="admin-creator-toggle">
                <input
                  type="checkbox"
                  id="hide"
                  name="hide"
                  checked={formData.hide === 1}
                  onChange={handleCheckboxChange}
                />
                <span>{t('adminCreatorDetail.hideProfile')}</span>
              </label>

              <label className="admin-creator-toggle">
                <input
                  type="checkbox"
                  id="isTrusted"
                  name="isTrusted"
                  checked={formData.isTrusted === 1}
                  onChange={handleCheckboxChange}
                />
                <span>{t('adminCreatorDetail.isTrusted')}</span>
              </label>
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-title">{t('adminCreatorDetail.bio')}</h2>

            <div className="form-group">
              <div className="clearable-field clearable-field-textarea">
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={12}
                  className="form-textarea admin-creator-bio clearable-textarea"
                />
                {formData.description && (
                  <button
                    type="button"
                    className="clearable-field-button"
                    onClick={() => setFormData((prev) => ({ ...prev, description: '' }))}
                    aria-label={t('common.clearSearch')}
                    title={t('common.clearSearch')}
                  >
                    x
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="form-section">
            <h2 className="section-title">{t('adminCreatorDetail.linksApps')}</h2>

            <div className="admin-creator-grid">
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.appName')}</label>
                <Input name="app" value={formData.app} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('adminCreatorDetail.appUrl')}</label>
                <Input name="url_app" value={formData.url_app} onChange={handleChange} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('adminCreatorDetail.youtubeUrl')}</label>
              <Input name="url_youtube" value={formData.url_youtube} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">{t('adminCreatorDetail.instagramUrl')}</label>
              <Input name="url_instagram" value={formData.url_instagram} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label className="form-label">{t('adminCreatorDetail.otherUrl')}</label>
              <Input name="url_other" value={formData.url_other} onChange={handleChange} />
            </div>
          </section>

          <div className="form-actions">
            <Button type="submit" variant="secondary" disabled={saving}>
              {saving ? t('adminCreatorDetail.saving') : t('adminCreatorDetail.saveProfile')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCreatorDetailPage;
